import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import LineChart from '../../../components/charts/LineChart';
import supabase from '../../../supabase/supabase';
import { Button } from '../../../components/ui/Button';
import { reportService } from '../../../services/reportService';
import * as FiIcons from 'react-icons/fi';

const { FiDownload, FiChevronDown, FiFileText, FiTable, FiCalendar } = FiIcons;

export default function AdminOverview() {
  const [stats, setStats] = useState({ users: 0, barbers: 0, bookings: 0, revenue: 0 });
  const [chartData, setChartData] = useState({ data: [], labels: [] });
  const [timeFilter, setTimeFilter] = useState(''); 
  const [rawBookings, setRawBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      let userQuery = supabase.from('profiles_1740423033529').select('id, created_at', { count: 'exact' }).eq('role', 'customer');
      let barberQuery = supabase.from('profiles_1740423033529').select('id, created_at', { count: 'exact' }).eq('role', 'barber');
      let bookingQuery = supabase.from('bookings_1740423033529').select('id, created_at, status, total_price, booking_date, booking_time, payment_status, customer_id, barber_id');

      if (timeFilter) {
        const start = new Date(`${timeFilter}-01T00:00:00Z`);
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999);
        
        userQuery = userQuery.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
        barberQuery = barberQuery.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
        bookingQuery = bookingQuery.gte('created_at', start.toISOString()).lte('created_at', end.toISOString());
      }

      const [usersRes, barbersRes, bookingsRes] = await Promise.all([userQuery, barberQuery, bookingQuery]);
      
      const allBookings = bookingsRes.data || [];
      setRawBookings(allBookings);
      const completedBookings = allBookings.filter(b => b.status === 'completed');
      const revenue = completedBookings.reduce((sum, b) => sum + Number(b.total_price || 0), 0);

      setStats({
        users: usersRes.data?.length || 0,
        barbers: barbersRes.data?.length || 0,
        bookings: allBookings.length,
        revenue: revenue
      });

      if (timeFilter) {
        const daysInMonth = new Date(parseInt(timeFilter.split('-')[0]), parseInt(timeFilter.split('-')[1]), 0).getDate();
        const dailyRevenue = Array(daysInMonth).fill(0);
        const labels = Array.from({length: daysInMonth}, (_, i) => `${i + 1}`);
        
        completedBookings.forEach(b => {
          const day = new Date(b.created_at).getDate() - 1;
          dailyRevenue[day] += Number(b.total_price);
        });
        
        setChartData({ data: dailyRevenue, labels });
      } else {
        const monthlyRevenue = Array(12).fill(0);
        const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        completedBookings.forEach(b => {
          const month = new Date(b.created_at).getMonth();
          monthlyRevenue[month] += Number(b.total_price);
        });
        
        setChartData({ data: monthlyRevenue, labels });
      }
      
      setLoading(false);
    };

    fetchStats();
  }, [timeFilter]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowExportOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExport = (format) => {
    const fileName = `BookOTG_Revenue_Report_${timeFilter || 'All_Time'}`;
    const reportTitle = timeFilter 
      ? `Platform Performance Report - ${new Date(timeFilter).toLocaleString('default', { month: 'long', year: 'numeric' })}`
      : 'Lifetime Platform Performance Report';

    if (format === 'xlsx') {
      reportService.exportToExcel(rawBookings, fileName);
    } else {
      reportService.exportToPDF(rawBookings, reportTitle, fileName);
    }
    setShowExportOptions(false);
  };

  const cards = [
    { label: 'Total Users', value: stats.users, icon: 'Users', color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Total Barbers', value: stats.barbers, icon: 'Scissors', color: 'text-green-500', bg: 'bg-green-50' },
    { label: 'Total Bookings', value: stats.bookings, icon: 'Calendar', color: 'text-purple-500', bg: 'bg-purple-50' },
    { label: 'Total Revenue', value: 'Rs. ' + stats.revenue.toLocaleString(), icon: 'DollarSign', color: 'text-brand-primary', bg: 'bg-brand-primary/10' },
  ];

  return (
    <div className="animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Admin Overview</h1>
          <p className="text-gray-500 font-medium italic mt-1">Platform performance metrics.</p>
        </div>
        
        <div className="flex items-center gap-3 bg-white p-2 rounded-2xl border border-gray-200 shadow-sm relative">
          <SafeIcon icon={FiCalendar} className="text-gray-400 ml-2" />
          <input 
            type="month" 
            value={timeFilter} 
            onChange={(e) => setTimeFilter(e.target.value)}
            className="border-none outline-none bg-transparent font-black text-brand-dark px-2 cursor-pointer text-sm"
          />
          {timeFilter && (
            <button onClick={() => setTimeFilter('')} className="text-[10px] text-brand-primary font-black uppercase tracking-widest px-3 hover:underline">
              Clear
            </button>
          )}
          <div className="w-px h-6 bg-gray-200 mx-1"></div>
          
          {/* Export Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <Button 
              onClick={() => setShowExportOptions(!showExportOptions)} 
              variant="outline" 
              className="h-10 text-[10px] px-5 border-transparent bg-brand-dark text-white hover:bg-brand-black transition-all font-black uppercase tracking-widest rounded-xl flex items-center gap-2"
            >
              <SafeIcon icon={FiDownload} />
              Export Report
              <SafeIcon icon={FiChevronDown} className={showExportOptions ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </Button>

            {showExportOptions && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 py-2 animate-in slide-in-from-top-2 overflow-hidden">
                <button 
                  onClick={() => handleExport('pdf')}
                  className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                >
                  <SafeIcon icon={FiFileText} className="text-red-500 text-lg" />
                  Download PDF
                </button>
                <button 
                  onClick={() => handleExport('xlsx')}
                  className="w-full text-left px-5 py-3.5 text-[11px] font-black uppercase tracking-widest text-gray-600 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                >
                  <SafeIcon icon={FiTable} className="text-green-600 text-lg" />
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-20 text-center">
          <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">Analyzing Data...</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {cards.map((stat, i) => (
              <div key={i} className="bg-white p-7 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5">
                <div className={`w-14 h-14 rounded-2xl ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                  <SafeIcon name={stat.icon} className={`text-2xl ${stat.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-black text-brand-dark tracking-tight">{stat.value}</p>
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-0.5">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-xl font-black text-brand-dark">Revenue Growth (LKR)</h2>
                <p className="text-xs text-gray-400 font-medium">{timeFilter ? 'Daily Performance' : 'Monthly Performance'}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-brand-primary rounded-full"></div>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Completed Sales</span>
              </div>
            </div>
            <LineChart data={chartData.data} labels={chartData.labels} title="Revenue" color="#0047FF" />
          </div>
        </>
      )}
    </div>
  );
}